/**
 * Экраны Chat грузятся по требованию, а роутер ждёт кусок кода до показа экрана: если куска ещё нет, переход «залипает».
 * Чтобы переключение разделов не ждало сети, подтягиваем экраны заранее:
 *  1) частые — сразу, когда вкладка простаивает;
 *  2) остальные разделы — следом, по одному, без нагрузки на сеть;
 *  3) любой раздел — в момент наведения или нажатия на ссылку (раньше самого щелчка).
 * Экономия трафика и медленная сеть — не трогаем: тогда экран грузится только при заходе.
 */
function saving(): boolean {
  const c: unknown = Reflect.get(navigator, 'connection');
  if (typeof c !== 'object' || c === null) return false;
  const type: unknown = Reflect.get(c, 'effectiveType');
  return Reflect.get(c, 'saveData') === true || type === 'slow-2g' || type === '2g';
}

type Loader = () => Promise<unknown>;

const hot: readonly Loader[] = [
  () => import('./WorkspaceLayout'),
  () => import('@/features/conversation/ConversationPage'),
  () => import('@/features/inbox/DmsPage'),
  () => import('@/features/home/MyDayPage'),
];

/** Разделы по адресу (после /w/<пространство>/): для подгрузки по наведению и в простое. */
const sections: readonly (readonly [RegExp, Loader])[] = [
  [/^docs\/sync/, () => import('@/features/docs/sync/SyncPage')],
  [/^docs\/[^/]+/, () => import('@/features/docs/DocumentPage')],
  [/^docs/, () => import('@/features/docs/DocsPage')],
  [/^calendar/, () => import('@/features/calendar/CalendarPage')],
  [/^clients/, () => import('@/features/clients/ClientsPage')],
  [/^meet\/[^/]+/, () => import('@/features/conference/MeetingRoomPage')],
  [/^meet/, () => import('@/features/conference/MeetingsPage')],
  [/^assistant/, () => import('@/features/assistant/AssistantPage')],
  [/^analytics/, () => import('@/features/analytics/AnalyticsPage')],
  [/^(threads|unreads|drafts|activity|saved|todos|search)/, () => import('@/features/inbox/ThreadsPage')],
  [/^people/, () => import('@/features/people/PeopleDirectory')],
  [/^settings/, () => import('@/features/settings/SettingsPage')],
  [/^channels/, () => import('@/features/channels/BrowseChannelsPage')],
  [/^screenshots/, () => import('@/features/screenshots/ScreenshotsPage')],
];

/**
 * Окна и панели, вынесенные из кода открытия беседы (lazyComponent): прогреваем первыми в простое,
 * чтобы первое открытие было мгновенным, как раньше.
 */
const dialogs: readonly Loader[] = [
  () => import('@/features/conversation/preview/FilePreviewPanel'),
  () => import('@/features/huddle/HuddleWindow'),
  () => import('@/features/calendar/EventDialog'),
  () => import('@/features/assistant/Markdown'),
  () => import('@/features/assistant/SettingsDialog'),
  () => import('@/features/calendar/ChatFreeTime'),
  () => import('@/features/booking/BookingLinkDialog'),
  () => import('@/features/dashboards/DashboardView'),
  () => import('@/features/sheets/dash/build'),
  () => import('@/features/integrations/AddAppDialog'),
  () => import('@/features/dashboards/AddDashboardDialog'),
  () => import('@/features/screenshots/ScreenshotDialog'),
  () => import('@/features/interpreter/InterpreterDialog'),
];

/** Остальные разделы — в простое, по одному (сначала окна из списка выше). */
const warm: readonly Loader[] = [
  ...dialogs,
  () => import('@/features/docs/DocsPage'),
  () => import('@/features/calendar/CalendarPage'),
  () => import('@/features/clients/ClientsPage'),
  () => import('@/features/conference/MeetingsPage'),
  () => import('@/features/assistant/AssistantPage'),
  () => import('@/features/inbox/ThreadsPage'),
  () => import('@/features/inbox/ActivityPage'),
  () => import('@/features/inbox/TodosPage'),
  () => import('@/features/people/PeopleDirectory'),
  () => import('@/features/analytics/AnalyticsPage'),
  () => import('@/features/settings/SettingsPage'),
  () => import('@/features/docs/DocumentPage'),
];

function idle(run: () => void, timeout: number): void {
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout });
  else setTimeout(run, 2000);
}

function quiet(load: Loader): Promise<unknown> {
  return load().catch(() => undefined);
}

export function prefetchHotScreens(): void {
  if (saving()) return;
  idle(() => {
    for (const load of hot) void quiet(load);
    // Остальное — цепочкой: следующий кусок грузится, когда предыдущий пришёл и вкладка снова свободна.
    let i = 0;
    const next = (): void => {
      const load = warm[i++];
      if (load === undefined) return;
      void quiet(load).then(() => { idle(next, 3000); });
    };
    idle(next, 6000);
  }, 4000);
}

/** Адрес раздела внутри пространства: `#/w/x/docs` (демо), `/w/x/docs` (сайт). */
function sectionOf(href: string): string | null {
  const m = /\/w\/[^/?#]+\/([^?#]*)/.exec(href);
  return m?.[1] ?? null;
}

const asked = new Set<Loader>();

/** Наведение, касание или фокус на ссылке раздела — подгружаем его экран до щелчка. */
export function prefetchOnIntent(target: EventTarget | null): void {
  if (!(target instanceof Element) || saving()) return;
  const href = target.closest('a')?.getAttribute('href');
  if (href === null || href === undefined) return;
  const path = sectionOf(href);
  if (path === null) return;
  const hit = sections.find(([re]) => re.test(path));
  if (hit === undefined || asked.has(hit[1])) return;
  asked.add(hit[1]);
  void quiet(hit[1]);
}

export function watchIntent(): void {
  for (const ev of ['pointerover', 'pointerdown', 'focusin'] as const) {
    document.addEventListener(ev, (e) => { prefetchOnIntent(e.target); }, { passive: true });
  }
}
